import {transparentize} from 'polished';
import React, {FC} from 'react';
import styled from 'styled-components';

import {
  CommentAnnotationModel,
  MessageAnnotationModel,
} from '../../../../libs/shared/core/src/models/api/annotationModel';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, ellipsisAfterNumberOfLines} from '../../../../libs/shared/ui/src/styles/mixins';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {buildContactDisplayHandle, buildContactDisplayName} from '../../../helpers/models/contactHelpers';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';

interface AnnotationSummaryProps {
  annotation: MessageAnnotationModel | CommentAnnotationModel;
  author?: TeammateModel;
  referencedRecipient?: VisualRecipientModel;
  shouldTruncateText?: boolean;
  shouldShowOnOneLine?: boolean;
  shouldHideRecipient?: boolean;
}

/*
 * Style.
 */

const StyledContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
  margin: 6px 4px 13px 8px;
`;

const StyledContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 4px;
  width: 100%;
`;

const StyledInfoDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.verySmall};
  ${ellipsis()};
`;

const StyledDescriptionDiv = styled.div`
  margin-top: 3px;

  color: ${(p) => p.theme.greys.shade60};
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.verySmall};
  ${ellipsis()};
`;

const StyledTextDiv = styled.div<{
  $shouldTruncateText?: boolean;
  $shouldShowOnOneLine?: boolean;
  $shouldHideRecipient?: boolean;
}>`
  margin-top: ${(p) => (p.$shouldHideRecipient ? '1px' : '10px')};
  line-height: 1.4;
  padding-left: 12px;
  position: relative;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    display: block;
    left: 0;
    top: 0;
    bottom: 0;
    width: 0;
    border: 1px solid ${(p) => p.theme.greys.shade50};
    border-radius: 1px;
  }

  &,
  mark {
    color: ${(p) => p.theme.greys.shade80};
    font-size: ${fontSizes.small};
    font-weight: ${fontWeights.normal};
    max-width: 100%;
  }

  mark {
    ${(p) =>
      (p.$shouldTruncateText || p.$shouldShowOnOneLine) &&
      ellipsisAfterNumberOfLines(p.$shouldShowOnOneLine ? 1 : 2, true)};
    background-color: ${transparentize(0.8, '#FF5C00')};
    ${(p) =>
      boxShadow(
        p.theme,
        `-2px 0 ${transparentize(0.8, '#FF5C00')}, 2px 0 ${transparentize(0.8, '#FF5C00')}`,
      )};
  }
`;

export const AnnotationSummary: FC<AnnotationSummaryProps> = (props) => {
  const {annotation, author, referencedRecipient, shouldHideRecipient} = props;

  return (
    <StyledContainerDiv>
      <StyledContentDiv>
        {!shouldHideRecipient && renderPersonHeader(author, referencedRecipient)}
        <StyledTextDiv
          $shouldTruncateText={props.shouldTruncateText}
          $shouldShowOnOneLine={props.shouldShowOnOneLine}
          $shouldHideRecipient={props.shouldHideRecipient}
        >
          <mark>{annotation.text}</mark>
        </StyledTextDiv>
      </StyledContentDiv>
    </StyledContainerDiv>
  );
};

function renderPersonHeader(
  author: TeammateModel | undefined,
  visualContact: VisualRecipientModel | undefined,
) {
  if (author) {
    return (
      <StyledInfoDiv>
        <StyledTitleDiv>{buildTeammateDisplayName(author)}</StyledTitleDiv>
        <StyledDescriptionDiv>{author.email}</StyledDescriptionDiv>
      </StyledInfoDiv>
    );
  }

  if (visualContact) {
    const name = buildContactDisplayName(visualContact);
    const handle = buildContactDisplayHandle(visualContact);

    return (
      <StyledInfoDiv>
        <StyledTitleDiv>{name}</StyledTitleDiv>
        <StyledDescriptionDiv>{handle}</StyledDescriptionDiv>
      </StyledInfoDiv>
    );
  }

  return null;
}
